import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
    LoginManager,
    UserService,
    UserStatus
} from '@flyfreely-portal-ui/flyfreely';
import { firstValueFrom, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

/**
 * This authentication guard is responsible for initialiating a login if required, via the `LoginManager`.
 *
 * This behaviour can be controlled on a per route basis by setting the `data.unauthenticatedAction` property in the route configuration
 * to either `login`, `signup`, or `none`. This defaults to `login`.
 */
@Injectable()
export class AuthenticatedGuard {
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private userService: UserService,
        private loginManager: LoginManager
    ) {}

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    async canActivate(route: ActivatedRouteSnapshot) {
        if (this.userService.getUserStatus() === UserStatus.LOGGED_IN) {
            return true;
        }
        if (
            this.userService.getUserStatus() === UserStatus.UNKNOWN ||
            this.userService.getUserStatus() === UserStatus.LOADING
        ) {
            // wait until changed
            const newStatus = await firstValueFrom(
                this.userService.userChange$.pipe(
                    filter(
                        c =>
                            c.type !== UserStatus.UNKNOWN &&
                            c.type !== UserStatus.LOADING
                    ),
                    takeUntil(this.ngUnsubscribe$)
                )
            );
            if (newStatus.type === UserStatus.LOGGED_IN) {
                return true;
            }
        }

        const unauthenticatedAction: 'login' | 'signup' | 'none' =
            route.data?.unauthenticatedAction ?? 'login';
        if (unauthenticatedAction === 'signup') {
            this.loginManager.register();
        } else if (unauthenticatedAction === 'login') {
            this.loginManager.login();
        }
        return false;
    }
}
